// Pure XP/level calculations for the levels system. No network or storage
// I/O here - see useUserXp.ts for pulling the progress snapshots this
// operates on from the cloud. See AGENTS.md for implementation checkpoints.
// Levels reuse existing per-user feature rows rather than a separate score.

export type LearnedSet = Record<string, string>;
export type CountMap = Record<string, number>;
export type NestedCountMap = Record<string, Record<string, number>>;

export type ProgressSnapshots = Partial<{
    lessonProgress: LearnedSet;
    grammarProgress: LearnedSet;
    vocabularyProgress: LearnedSet;
    kanjiMemory: LearnedSet;
    kanaDrawCounts: CountMap;
    kanjiDrawCounts: CountMap;
    kanaListenCounts: CountMap;
    kanjiListenCounts: CountMap;
    kanjiMemoryGuessStats: NestedCountMap;
    speakingCounts: CountMap;
}>;

export type XpBreakdownEntry = {
    feature: keyof ProgressSnapshots;
    label: string;
    xp: number;
    // How many actual items (lessons, kanji, words...) that XP came from,
    // and the noun for them - e.g. "68 kanji" alongside "1360 XP". null for
    // repetition-based entries (draw/listen/speak reps, quiz guesses), where
    // the count would just restate the XP under a different cap and isn't a
    // meaningful stat on its own.
    count: number;
    unit: string | null;
};

export type XpBreakdown = {
    total: number;
    entries: XpBreakdownEntry[];
};

// Repetition-based features (draw/listen/speak reps, quiz guesses) are
// capped per item so idly repeating one character can't grind XP forever.
// "Learned"-style features (lessons, vocab, grammar, kanji marked known)
// don't need a cap - each item can only be learned once.
const DEFAULT_REP_CAP = 10;

function isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

function validCount(value: unknown, cap: number): number {
    return typeof value === "number" && Number.isFinite(value) && value > 0
        ? Math.min(Math.floor(value), cap) : 0;
}

function xpFromLearnedSet(data: LearnedSet | undefined, xpEach: number, matchValue = "learned"): { xp: number; count: number } {
    if (!isRecord(data)) return { xp: 0, count: 0 };
    const count = Object.keys(data).filter((key) => data[key] === matchValue).length;
    return { xp: count * xpEach, count };
}

function xpFromCounts(data: CountMap | undefined, xpPerRep: number, cap: number = DEFAULT_REP_CAP): number {
    if (!isRecord(data)) return 0;
    return Object.values(data).reduce((sum, n) => sum + validCount(n, cap) * xpPerRep, 0);
}

function xpFromNestedCounts(data: NestedCountMap | undefined, xpPerRep: number, cap: number = DEFAULT_REP_CAP): number {
    if (!isRecord(data)) return 0;
    return Object.values(data).reduce(
        (sum, fields) => sum + xpFromCounts(fields, xpPerRep, cap),
        0
    );
}

export function computeXpBreakdown(snapshots: ProgressSnapshots): XpBreakdown {
    const lessons = xpFromLearnedSet(snapshots.lessonProgress, 25);
    const grammar = xpFromLearnedSet(snapshots.grammarProgress, 15);
    const vocabulary = xpFromLearnedSet(snapshots.vocabularyProgress, 5);
    // Only "known" counts - "learning" means the user flagged it as
    // still in progress, not an accomplishment yet.
    const kanjiKnown = xpFromLearnedSet(snapshots.kanjiMemory, 20, "known");

    const entries: XpBreakdownEntry[] = [
        { feature: "lessonProgress", label: "Lessons completed", xp: lessons.xp, count: lessons.count, unit: lessons.count === 1 ? "lesson" : "lessons" },
        { feature: "grammarProgress", label: "Grammar points learned", xp: grammar.xp, count: grammar.count, unit: grammar.count === 1 ? "grammar point" : "grammar points" },
        { feature: "vocabularyProgress", label: "Vocabulary learned", xp: vocabulary.xp, count: vocabulary.count, unit: vocabulary.count === 1 ? "word" : "words" },
        { feature: "kanjiMemory", label: "Kanji marked known", xp: kanjiKnown.xp, count: kanjiKnown.count, unit: "kanji" },
        { feature: "kanaDrawCounts", label: "Kana writing practice", xp: xpFromCounts(snapshots.kanaDrawCounts, 1), count: 0, unit: null },
        { feature: "kanjiDrawCounts", label: "Kanji writing practice", xp: xpFromCounts(snapshots.kanjiDrawCounts, 1), count: 0, unit: null },
        { feature: "kanaListenCounts", label: "Kana listening practice", xp: xpFromCounts(snapshots.kanaListenCounts, 1, 5), count: 0, unit: null },
        { feature: "kanjiListenCounts", label: "Kanji listening practice", xp: xpFromCounts(snapshots.kanjiListenCounts, 1, 5), count: 0, unit: null },
        {
            feature: "kanjiMemoryGuessStats",
            label: "Kanji quiz correct guesses",
            xp: xpFromNestedCounts(snapshots.kanjiMemoryGuessStats, 2, 5),
            count: 0,
            unit: null,
        },
        { feature: "speakingCounts", label: "Speaking practice", xp: xpFromCounts(snapshots.speakingCounts, 2, 5), count: 0, unit: null },
    ];
    const total = entries.reduce((sum, entry) => sum + entry.xp, 0);
    return { total, entries };
}

// Cumulative XP required to REACH a given level. Level 1 starts at 0 XP;
// each subsequent level needs progressively more, following a n^1.6 curve
// (steeper than linear so early levels come quickly but later ones take
// real, sustained practice) rounded to the nearest 10 for tidy numbers.
// Tune this freely - nothing else depends on the exact shape of the curve.
export function xpForLevel(level: number): number {
    if (level <= 1) return 0;
    return Math.round((100 * level ** 1.6) / 10) * 10;
}

export function levelForXp(xp: number): number {
    if (!Number.isFinite(xp) || xp < 0) return 1;
    xp = Math.min(xp, Number.MAX_SAFE_INTEGER);
    let low = 1;
    let high = 2;
    while (xpForLevel(high) <= xp) high *= 2;
    while (high - low > 1) {
        const middle = Math.floor((low + high) / 2);
        if (xpForLevel(middle) <= xp) low = middle;
        else high = middle;
    }
    return low;
}

export type XpProgress = {
    level: number;
    xp: number;
    currentLevelXp: number;
    nextLevelXp: number;
    xpIntoLevel: number;
    xpForNextLevel: number;
};

export function xpProgress(xp: number): XpProgress {
    xp = Number.isFinite(xp) && xp > 0 ? Math.min(Math.floor(xp), Number.MAX_SAFE_INTEGER) : 0;
    const level = levelForXp(xp);
    const currentLevelXp = xpForLevel(level);
    const nextLevelXp = xpForLevel(level + 1);
    return {
        level,
        xp,
        currentLevelXp,
        nextLevelXp,
        xpIntoLevel: xp - currentLevelXp,
        xpForNextLevel: nextLevelXp - currentLevelXp,
    };
}
